/**
 * <QuickOrderFlow />
 *
 * Shared Quick Order experience used by:
 *  - Authenticated My Orders flow (rendered inside ProductConfigWizard)
 *  - Public campaign landing pages (/campaigns/:slug)
 *
 * Steps:
 *  0. Choose Your Package
 *  1. How many copies?
 *  2. Review Your Package
 *
 * Props:
 *  - bundles:           array of bundle objects (already filtered/sorted by caller)
 *  - productFamily:     "vinyl" | "cd" | "dvd" | "bluray" (for labels only)
 *  - initialBundleId:   optional — auto-select this bundle and skip to step 1
 *  - startingStep:      "packages" (default) | "quantity"
 *  - requireTitle:      when true, a Title input is shown on the quantity step
 *                       and the user must fill it before continuing.
 *  - initialTitle:      pre-fill for the title input
 *  - onComplete(specs, priceEstimate, meta) — called when user clicks "Continue with this package"
 *  - onBack:            optional — shown as "Change mode" on step 0
 *  - submitLabel:       optional override for the final CTA copy
 *
 * Uses public endpoints `/api/sales/quote` (no auth) and `/api/pricing/currencies` (no auth).
 */
import { useEffect, useMemo, useState } from 'react';
import {
    Card, CardContent, CardDescription, CardHeader, CardTitle,
} from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import {
    AlertCircle, Crown, Zap, Sparkles, Check, ChevronLeft, ChevronRight, RefreshCw,
} from 'lucide-react';
import { API_BASE } from '../lib/apiBase';
import { useCurrency, formatCurrencyAmount, convertFromEur } from '../hooks/useCurrency';
import { useTranslation } from '../contexts/TranslationContext';

const GOLD = 'linear-gradient(135deg, hsl(35 92% 45%), hsl(42 96% 55%))';

const tierBadgeFor = (tier) => {
    switch (tier) {
        case 'budget':
            return { label: 'Budget', className: 'bg-slate-100 text-slate-600' };
        case 'standard':
            return { label: 'Standard', className: 'bg-amber-100 text-amber-800 border border-amber-200' };
        case 'premium':
            return { label: 'Premium', className: 'bg-violet-100 text-violet-700 border border-violet-200' };
        default:
            return { label: tier || 'Bundle', className: 'bg-slate-100 text-slate-600' };
    }
};

// Format an EUR amount in the customer's currency. For the EUR case we
// stick with the rounded display value (the customer sees "6,12 €"). For any
// other currency we prefer `eurRaw` — the backend's un-rounded basis — so
// the conversion isn't biased by the EUR display rounding (otherwise
// 6,115 EUR → display 6,12 EUR → DKK 45,75 instead of the mathematically
// correct DKK 45,70). Falls back to `eur` when no raw value is available.
const formatKrFactory = (rates, customerCurrency) => (eur, decimals = 0, eurRaw = null) => {
    if (eur === null || eur === undefined) return '—';
    if (!rates || customerCurrency === 'EUR') {
        return formatCurrencyAmount(eur, customerCurrency, { decimals });
    }
    const basis = (eurRaw !== null && eurRaw !== undefined) ? eurRaw : eur;
    const local = convertFromEur(basis, customerCurrency, rates);
    return formatCurrencyAmount(local, customerCurrency, { decimals });
};

async function fetchQuote(bundle, quantity) {
    if (!bundle?.base_sales_sku) return null;
    const res = await fetch(`${API_BASE}/api/sales/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            quantity,
            line_items: [
                { sales_sku: bundle.base_sales_sku, quantity_multiplier: 1 },
                ...(bundle.default_addon_skus || []).map((sku) => ({ sales_sku: sku, quantity_multiplier: 1 })),
            ],
        }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    // Quote-gated bundle short-circuit. The backend sets
    // ``requires_quote=true`` + nulls out the numeric estimates so the
    // customer surface never leaks a bogus €0 total — we forward the
    // signal + the triggering SKU list to the UI which switches to a
    // "Request a Quote" CTA.
    if (data.requires_quote) {
        return {
            total: null,
            unit: null,
            totalRaw: null,
            unitRaw: null,
            quantity,
            requires_quote: true,
            requires_quote_source: data.requires_quote_source || null,
            quote_gated_skus: data.quote_gated_skus || [],
        };
    }
    return {
        total: data.total_estimate || 0,
        unit: data.unit_price_estimate || 0,
        // Un-rounded versions for FX conversion (see formatKrFactory).
        // Fall back to the rounded values if backend doesn't carry them yet.
        totalRaw: (data.total_raw ?? data.total_estimate) || 0,
        unitRaw: (data.unit_price_raw ?? data.unit_price_estimate) || 0,
        quantity,
        requires_quote: false,
    };
}

export default function QuickOrderFlow({
    bundles,
    productFamily = 'vinyl',
    initialBundleId = null,
    startingStep = 'packages',
    requireTitle = false,
    initialTitle = '',
    onComplete,
    onBack,
    submitLabel,
    compact = false,
    currencyOverride = null,
    // Landing page "Show Prices" OFF → hide every price/quote amount in
    // the Quick Order flow. Quantities, bundles, specs, timelines and
    // navigation/CTAs are untouched. Defaults true.
    showPrices = true,
}) {
    const { t } = useTranslation();
    const familyLabel = productFamily === 'cd' ? 'CD' : productFamily === 'dvd' ? 'DVD'
        : productFamily === 'bluray' ? 'Blu-ray' : 'vinyl';
    const isVinyl = (productFamily || 'vinyl').toLowerCase() === 'vinyl';

    // Use the SAME currency hook as ProductConfigWizard.QuickOrderFlow so that
    // campaign pricing matches internal Quick Order pricing exactly
    // (live ECB rate + 0.05 rounding + locale formatter).
    // If the visitor is logged in (e.g. an authenticated customer browsing a
    // campaign link), we pass their token so they see their preferred currency;
    // otherwise the hook gracefully falls back to EUR via the public endpoint.
    // `currencyOverride` (when provided by the caller, e.g. CampaignLandingPage
    // reading `?currency=` from the URL) takes precedence over both.
    const authToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const { rates, customerCurrency: hookCurrency } = useCurrency(authToken);
    const customerCurrency = currencyOverride || hookCurrency;
    const formatKr = useMemo(
        () => formatKrFactory(rates, customerCurrency),
        [rates, customerCurrency],
    );

    // Hide the tier badge when every displayed bundle shares the same tier
    // (a "Standard"/... chip on every card is redundant noise).
    const allSameTier = bundles.length > 0
        && new Set(bundles.map((b) => b.tier || '')).size <= 1;

    const [step, setStep] = useState(startingStep === 'quantity' && initialBundleId ? 1 : 0);
    const [selectedBundle, setSelectedBundle] = useState(
        initialBundleId ? bundles.find((b) => b.id === initialBundleId) || null : null,
    );
    const [quantity, setQuantity] = useState(
        selectedBundle?.default_quantity || 500,
    );
    const [price, setPrice] = useState(null);
    const [loadingPrice, setLoadingPrice] = useState(false);
    const [bundlePrices, setBundlePrices] = useState({});
    const [title, setTitle] = useState(initialTitle);
    const [showDetails, setShowDetails] = useState(false);
    // Quote-request comment — only used when the selected bundle's
    // pricing is quote-gated. Kept in local state so the user can type
    // a note before submitting. The field is never shown for fixed-
    // price bundles.
    const [quoteComment, setQuoteComment] = useState('');

    // When a bundle gets auto-selected via initialBundleId, kick off pricing
    useEffect(() => {
        if (!selectedBundle) return;
        let cancelled = false;
        setLoadingPrice(true);
        fetchQuote(selectedBundle, quantity).then((p) => {
            if (cancelled) return;
            setPrice(p);
            setBundlePrices((prev) => ({ ...prev, [selectedBundle.id]: p }));
            setLoadingPrice(false);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBundle?.id, quantity]);

    // Lazy-load prices for all bundles when on step 0 (default qty)
    useEffect(() => {
        if (step !== 0) return;
        let cancelled = false;
        (async () => {
            for (const b of bundles) {
                if (cancelled) return;
                if (bundlePrices[b.id]) continue;
                const p = await fetchQuote(b, b.default_quantity || 500);
                if (cancelled) return;
                if (p) setBundlePrices((prev) => ({ ...prev, [b.id]: p }));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, bundles.length]);

    const handlePickBundle = (bundle) => {
        const qty = bundle.default_quantity || 500;
        setSelectedBundle(bundle);
        setQuantity(qty);
        setStep(1);
    };

    const presets = useMemo(() => {
        // Use the bundle's own allowed quantities when available — admins
        // configure these per bundle (e.g. 12" LP Standard → 250/300/500/1000).
        // Falls back to format-based defaults only for legacy bundles missing
        // the field so behaviour stays predictable for older fixtures.
        const bundleQuantities = selectedBundle?.allowed_quantity_choices;
        if (Array.isArray(bundleQuantities) && bundleQuantities.length > 0) {
            return bundleQuantities;
        }
        const fmtTokens = (selectedBundle?.format_label || selectedBundle?.bundled_options?.format || '').toString();
        if (fmtTokens.includes('7"')) return [100, 300, 500, 1000];
        return [100, 250, 500, 1000];
    }, [selectedBundle]);

    const handleFinalContinue = () => {
        if (!selectedBundle || !price) return;
        const isQuoteRequired = !!price.requires_quote;
        const specs = {
            format: selectedBundle.bundled_options?.format,
            quantity,
            ...selectedBundle.bundled_options,
            _bundle_id: selectedBundle.id,
            _bundle_title: selectedBundle.title,
            _bundle_skus: [
                selectedBundle.base_sales_sku,
                ...(selectedBundle.default_addon_skus || []),
            ],
            // Pass the quote-gate signal through so the parent routes to
            // ``POST /api/orders/request-quote`` instead of ``POST /orders``.
            _requires_quote: isQuoteRequired,
            _quote_gated_skus: price.quote_gated_skus || null,
        };
        const priceEstimate = isQuoteRequired
            ? { total_estimate: null, unit_price_estimate: null, requires_quote: true }
            : { total_estimate: price.total, unit_price_estimate: price.unit };
        const meta = {
            title: requireTitle ? title.trim() : initialTitle || null,
            bundle: selectedBundle,
            quantity,
            product_family: productFamily,
            // Only forwarded for quote-gated flows; LandingPage picks it
            // up when calling the request-quote endpoint.
            quote_comment: isQuoteRequired ? (quoteComment || '').trim() : null,
        };
        onComplete && onComplete(specs, priceEstimate, meta);
    };

    // ===== STEP 0: Choose Your Package =====
    if (step === 0) {
        return (
            <Card data-testid="qoflow-step-packages">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <Badge
                                className="mb-2 text-white border-0 shadow-sm"
                                style={{ backgroundImage: GOLD }}
                            >
                                Quick Order
                            </Badge>
                            <CardTitle>{t('quick_order.choose_package', 'Choose Your Package')}</CardTitle>
                            <CardDescription>Select a pre-configured {familyLabel} package</CardDescription>
                        </div>
                        {onBack && (
                            <Button variant="ghost" size="sm" onClick={onBack}>
                                Change mode
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {bundles.length === 0 ? (
                        <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{t('quick_order.no_packages', 'No packages available right now.')}</AlertDescription>
                        </Alert>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                            {bundles.map((bundle) => {
                                const tier = tierBadgeFor(bundle.tier);
                                const p = bundlePrices[bundle.id];
                                const isStd = bundle.tier === 'standard';
                                const isPrem = bundle.tier === 'premium';
                                return (
                                    <button
                                        key={bundle.id}
                                        onClick={() => handlePickBundle(bundle)}
                                        className={`group relative text-left p-5 rounded-2xl border-2 bg-white transition-all ${
                                            isPrem
                                                ? 'border-border hover:border-violet-400 hover:bg-violet-50/40'
                                                : isStd
                                                    ? 'border-border hover:border-amber-400 hover:bg-amber-50/40'
                                                    : 'border-border hover:border-slate-300'
                                        }`}
                                        data-testid={`qoflow-bundle-${bundle.id}`}
                                    >
                                        {!allSameTier && (
                                            <div className="flex items-center gap-2 mb-3">
                                                <Badge className={tier.className}>
                                                    {isPrem && <Crown className="h-3 w-3 mr-1" />}
                                                    {tier.label}
                                                </Badge>
                                            </div>
                                        )}
                                        <h3 className="text-lg font-semibold text-slate-900 mb-1">
                                            {bundle.title}
                                        </h3>
                                        {bundle.description && (
                                            <p className="text-sm text-slate-500 mb-2 line-clamp-2">
                                                {bundle.description}
                                            </p>
                                        )}
                                        {bundle.format_label && (
                                            <p className="text-xs text-slate-400 mb-3">{bundle.format_label}</p>
                                        )}
                                        {showPrices && (p?.requires_quote ? (
                                            <p className="text-sm text-amber-700 mb-2">
                                                <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] mr-1">
                                                    {t('quick_order.quote_required_badge', 'Quote Required')}
                                                </Badge>
                                            </p>
                                        ) : p && (
                                            <p className="text-sm text-slate-700 mb-2">
                                                From <span className="font-semibold">{formatKr(p.total, 0, p.totalRaw)}</span>
                                                <span className="text-xs text-slate-400 ml-1">
                                                    ({bundle.default_quantity || 500} copies)
                                                </span>
                                            </p>
                                        ))}
                                        <div className="flex items-center text-sm font-medium text-slate-700 mt-2 group-hover:translate-x-0.5 transition-transform">
                                            Pick this <ChevronRight className="h-4 w-4 ml-1" />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    // ===== STEP 1: How many copies? =====
    if (step === 1 && selectedBundle) {
        const titleValid = !requireTitle || title.trim().length > 0;

        if (compact) {
            // Conversion-optimised 2-column layout for public campaign pages
            return (
                <Card data-testid="qoflow-step-quantity">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <Badge
                                    className="mb-1 text-white border-0 shadow-sm"
                                    style={{ backgroundImage: GOLD }}
                                >
                                    Quick Order
                                </Badge>
                                <CardTitle className="text-xl">{t('quick_order.how_many_copies', 'How many copies?')}</CardTitle>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setStep(0)} data-testid="qoflow-back">
                                <ChevronLeft className="h-4 w-4 mr-1" /> Change package
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-2 pb-4">
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 min-h-[300px]">
                            {/* LEFT: Title + Quantity */}
                            <div className="lg:col-span-3 space-y-4">
                                {requireTitle && (
                                    <div>
                                        <Label htmlFor="qoflow-title" className="text-sm">{t('order.release_title', 'Release title')}</Label>
                                        <Input
                                            id="qoflow-title"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                    placeholder={t('order.title_placeholder_artist_album', 'e.g. Artist — Album Name')}
                                            className="mt-1"
                                            data-testid="qoflow-title-input"
                                        />
                                    </div>
                                )}
                                <div>
                                    <Label className="text-sm">{t('order.quantity_label', 'Quantity')}</Label>
                                    <div className="grid grid-cols-4 gap-2 mt-1">
                                        {presets.map((qty) => {
                                            const isSelected = quantity === qty;
                                            return (
                                                <button
                                                    key={qty}
                                                    onClick={() => setQuantity(qty)}
                                                    className={`px-2 py-2.5 rounded-lg border-2 bg-white transition-all hover:border-amber-400 hover:bg-amber-50/40 ${isSelected ? 'dark:!bg-amber-50 dark:!border-amber-500' : 'dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700'}`}
                                                    style={isSelected ? {
                                                        borderColor: 'hsl(38 92% 50%)',
                                                        backgroundColor: 'hsl(45 100% 96%)',
                                                        boxShadow: '0 0 0 3px hsl(42 96% 75% / 0.45)',
                                                    } : undefined}
                                                    data-testid={`qoflow-qty-${qty}`}
                                                >
                                                    <span className={`block text-lg font-bold leading-none ${isSelected ? 'text-slate-900 dark:text-slate-900' : 'text-slate-900 dark:text-white'}`} style={isSelected ? { color: 'hsl(222 47% 11%)' } : undefined}>{qty}</span>
                                                    <span className={`block text-[11px] mt-0.5 ${isSelected ? 'text-slate-500 dark:text-slate-600' : 'text-slate-500 dark:text-slate-400'}`} style={isSelected ? { color: 'hsl(215 16% 42%)' } : undefined}>copies</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT: Summary + Price + Timeline + CTA */}
                            <div className="lg:col-span-2 space-y-3">
                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                    <div className="text-center pb-3 border-b border-slate-200">
                                        {!showPrices ? null : loadingPrice ? (
                                            <RefreshCw className="h-5 w-5 mx-auto animate-spin text-slate-400" />
                                        ) : price?.requires_quote ? (
                                            <Badge className="bg-amber-200 text-amber-900 border-amber-300">
                                                {t('quick_order.quote_required_badge', 'Quote Required')}
                                            </Badge>
                                        ) : price ? (
                                            <>
                                                <p className="text-2xl font-bold text-slate-900 font-display leading-tight">
                                                    {formatKr(price.unit, 2, price.unitRaw)} <span className="text-sm font-medium text-slate-500">/ unit</span>
                                                </p>
                                                <p className="text-[11px] text-slate-500 mt-0.5">{formatKr(price.total, 0, price.totalRaw)} total</p>
                                            </>
                                        ) : (
                                                            <p className="text-sm text-slate-500">{t('quick_order.pricing_unavailable', 'Pricing unavailable')}</p>
                                        )}
                                    </div>
                                    {isVinyl && (
                                        <div className="flex justify-between text-[11px] mt-3">
                                            <span className="text-slate-500">{t('quick_order.test_press', 'Test press')}</span>
                                            <span className="text-slate-700 font-medium">~4 weeks</span>
                                        </div>
                                    )}
                                    <div className={`flex justify-between text-[11px] ${isVinyl ? 'mt-0.5' : 'mt-3'}`}>
                                        <span className="text-slate-500">{t('quick_order.production', 'Production')}</span>
                                        <span className="text-slate-700 font-medium">{isVinyl ? '~8 weeks' : '~2 weeks'}</span>
                                    </div>
                                </div>

                                <Button
                                    onClick={() => setStep(2)}
                                    disabled={!titleValid || !price}
                                    className="w-full h-11 text-white border-0 shadow-md hover:shadow-lg hover:brightness-105 transition-all"
                                    style={{ backgroundImage: GOLD }}
                                    data-testid="qoflow-next-review"
                                >
                                Review &amp; continue <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                                {requireTitle && !titleValid && (
                                    <p className="text-[11px] text-amber-700 text-center">{t('quick_order.enter_title_to_continue', 'Enter a release title to continue')}</p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        // Default (authenticated wizard) layout — unchanged
        return (
            <Card data-testid="qoflow-step-quantity">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <Badge
                                className="mb-2 text-white border-0 shadow-sm"
                                style={{ backgroundImage: GOLD }}
                            >
                                Quick Order
                            </Badge>
                            <CardTitle>{t('quick_order.how_many_copies', 'How many copies?')}</CardTitle>
                            <CardDescription>{t('quick_order.select_quantity', 'Select your production quantity')}</CardDescription>
                        </div>
                        <div className="text-right">
                            <p className="text-sm font-medium text-slate-700">{selectedBundle.title}</p>
                            <p className="text-xs text-slate-500">{selectedBundle.format_label}</p>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="max-w-md mx-auto py-6 space-y-6">
                        {requireTitle && (
                            <div>
                                <Label htmlFor="qoflow-title">{t('order.release_title', 'Release title')}</Label>
                                <Input
                                    id="qoflow-title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder={t('order.title_placeholder_artist_album', 'e.g. Artist — Album Name')}
                                    className="mt-1"
                                    data-testid="qoflow-title-input"
                                />
                                <p className="text-xs text-slate-500 mt-1">{t('quick_order.title_needed', 'Needed before we can save this draft.')}</p>
                            </div>
                        )}

                        <div className="grid grid-cols-4 gap-3">
                            {presets.map((qty) => {
                                const isSelected = quantity === qty;
                                return (
                                    <button
                                        key={qty}
                                        onClick={() => setQuantity(qty)}
                                        className={`p-4 rounded-xl border-2 bg-white transition-all hover:border-amber-400 hover:bg-amber-50/40 ${isSelected ? 'dark:!bg-amber-50 dark:!border-amber-500' : 'dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700'}`}
                                        style={isSelected ? {
                                            borderColor: 'hsl(38 92% 50%)',
                                            backgroundColor: 'hsl(45 100% 96%)',
                                            boxShadow: '0 0 0 3px hsl(42 96% 75% / 0.45)',
                                        } : undefined}
                                        data-testid={`qoflow-qty-${qty}`}
                                    >
                                        <span className={`block text-2xl font-bold ${isSelected ? 'text-slate-900 dark:text-slate-900' : 'text-slate-900 dark:text-white'}`} style={isSelected ? { color: 'hsl(222 47% 11%)' } : undefined}>{qty}</span>
                                        <span className={`block text-xs ${isSelected ? 'text-slate-500 dark:text-slate-600' : 'text-slate-500 dark:text-slate-400'}`} style={isSelected ? { color: 'hsl(215 16% 42%)' } : undefined}>copies</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Price card */}
                        <div className="bg-slate-50 rounded-xl p-4 text-center border border-slate-100">
                            {!showPrices ? null : loadingPrice ? (
                                <RefreshCw className="h-5 w-5 mx-auto animate-spin text-slate-400" />
                            ) : price?.requires_quote ? (
                                <Badge className="bg-amber-200 text-amber-900 border-amber-300">
                                    {t('quick_order.quote_required_badge', 'Quote Required')}
                                </Badge>
                            ) : price ? (
                                <>
                                    <p className="text-3xl font-bold text-slate-900 font-display" data-testid="quick-unit-price">
                                        {formatKr(price.unit, 2, price.unitRaw)} <span className="text-base font-medium text-slate-500">/ unit</span>
                                    </p>
                                    <p className="text-xs text-slate-500 mt-1" data-testid="quick-total-price">{formatKr(price.total, 0, price.totalRaw)} total</p>
                                </>
                            ) : (
                                <p className="text-sm text-slate-500">Pricing unavailable</p>
                            )}
                        </div>

                        {/* Timeline */}
                        <div className="bg-white rounded-lg px-4 py-3 border border-slate-100">
                            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1.5">
                                Estimated timeline
                            </p>
                            {isVinyl && (
                                <div className="flex justify-between text-xs">
                                    <span className="text-slate-500">Test press</span>
                                    <span className="text-slate-700 font-medium">~4 weeks</span>
                                </div>
                            )}
                            <div className={`flex justify-between text-xs ${isVinyl ? 'mt-1' : ''}`}>
                                <span className="text-slate-500">Production</span>
                                <span className="text-slate-700 font-medium">{isVinyl ? '~8 weeks' : '~2 weeks'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                        <Button variant="ghost" onClick={() => setStep(0)} data-testid="qoflow-back">
                            <ChevronLeft className="h-4 w-4 mr-1" /> Change package
                        </Button>
                        <Button
                            onClick={() => setStep(2)}
                            disabled={!titleValid || !price}
                            className="text-white border-0 shadow-md hover:shadow-lg hover:brightness-105 transition-all"
                            style={{ backgroundImage: GOLD }}
                            data-testid="qoflow-next-review"
                        >
                                Review <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // ===== STEP 2: Review Your Package =====
    if (step === 2 && selectedBundle) {
        const tier = tierBadgeFor(selectedBundle.tier);
        const specEntries = Object.entries(selectedBundle.bundled_options || {});

        if (compact) {
            // Conversion-optimised 2-column layout for public campaign pages
            return (
                <Card data-testid="qoflow-step-review">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <Badge
                                    className="mb-1 text-white border-0 shadow-sm"
                                    style={{ backgroundImage: GOLD }}
                                >
                                    Quick Order
                                </Badge>
                                <CardTitle className="text-xl">
                                    Your campaign package includes
                                </CardTitle>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setStep(1)} data-testid="qoflow-review-back">
                                <ChevronLeft className="h-4 w-4 mr-1" /> Change quantity
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-2 pb-4">
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 min-h-[300px]">
                            {/* LEFT: package + specs */}
                            <div className="lg:col-span-3 space-y-4">
                                <div>
                                    <Badge className={`${tier.className} text-[10px] mb-1`}>{tier.label}</Badge>
                                    <h3 className="text-lg font-bold text-slate-900">
                                        {selectedBundle.title}
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        {quantity} copies · {selectedBundle.format_label}
                                    </p>
                                    {requireTitle && title && (
                                        <p className="text-xs text-slate-600 mt-1">
                                            Release: <span className="font-medium text-slate-800">{title}</span>
                                        </p>
                                    )}
                                    {selectedBundle.description && (
                                        <p className="text-sm text-slate-600 mt-2 leading-relaxed whitespace-pre-line" data-testid="qoflow-review-description">
                                            {selectedBundle.description}
                                        </p>
                                    )}
                                </div>

                                {/* Included specs — inline, no boxed container */}
                                {specEntries.length > 0 && (
                                    <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
                                        {specEntries.map(([key, value]) => (
                                            <li key={key} className="flex items-center gap-2.5 min-w-0">
                                                <span className="h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                                                    <Check className="h-3.5 w-3.5 text-amber-600" strokeWidth={3} />
                                                </span>
                                                <span className="text-slate-700 truncate">
                                                    <span className="capitalize">{key.replace(/_/g, ' ')}: </span>
                                                    <span className="font-medium text-slate-900">{String(value)}</span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* RIGHT: price + timeline + CTA */}
                            <div className="lg:col-span-2 space-y-3">
                                {showPrices && (price?.requires_quote ? (
                                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200" data-testid="qoflow-review-quote-required">
                                        <div className="flex items-center gap-2 text-amber-900 mb-2">
                                            <Badge className="bg-amber-200 text-amber-900 border-amber-300">Quote Required</Badge>
                                        </div>
                                        <p className="text-sm text-amber-900 leading-snug">
                                            {t('quick_order.quote_required_msg_short', 'This configuration needs a custom quote.')}
                                        </p>
                                        {(price.quote_gated_skus || []).slice(0, 2).map((g, i) => (
                                            <p key={i} className="text-[11px] text-amber-800 mt-1">
                                                {g.procurement_item_name || g.display_name || g.sku}
                                            </p>
                                        ))}
                                        <label className="block mt-3 text-[11px] font-medium text-amber-900">
                                            {t('quick_order.quote_comment', 'Add a note for our team (optional)')}
                                        </label>
                                        <textarea
                                            value={quoteComment}
                                            onChange={(e) => setQuoteComment(e.target.value)}
                                            rows={3}
                                            placeholder={t('quick_order.quote_comment_ph', 'Deadline, special requests, etc.')}
                                            className="mt-1 w-full text-xs border border-amber-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                                            data-testid="qoflow-quote-comment"
                                            maxLength={2000}
                                        />
                                    </div>
                                ) : (
                                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                        <div className="text-center pb-3 border-b border-slate-200">
                                            {price ? (
                                                <>
                                                    <p className="text-2xl font-bold text-slate-900 font-display leading-tight">
                                                        {formatKr(price.unit, 2, price.unitRaw)} <span className="text-sm font-medium text-slate-500">/ unit</span>
                                                    </p>
                                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                                        {formatKr(price.total, 0, price.totalRaw)} total
                                                    </p>
                                                </>
                                            ) : (
                                                <p className="text-sm text-slate-500">{t('quick_order.pricing_unavailable', 'Pricing unavailable')}</p>
                                            )}
                                        </div>
                                        {isVinyl && (
                                            <div className="flex justify-between text-[11px] mt-3">
                                                <span className="text-slate-500">{t('quick_order.test_press', 'Test press')}</span>
                                                <span className="text-slate-700 font-medium">~4 weeks</span>
                                            </div>
                                        )}
                                        <div className={`flex justify-between text-[11px] ${isVinyl ? 'mt-0.5' : 'mt-3'}`}>
                                            <span className="text-slate-500">{t('quick_order.production', 'Production')}</span>
                                            <span className="text-slate-700 font-medium">{isVinyl ? '~8 weeks' : '~2 weeks'}</span>
                                        </div>
                                    </div>
                                ))}

                                <Button
                                    onClick={handleFinalContinue}
                                    disabled={!price}
                                    className="w-full h-11 owntune-cta btn-active transition-all"
                                    data-testid="qoflow-confirm"
                                >
                                    <Check className="h-4 w-4 mr-2" />
                                    {price?.requires_quote
                                        ? t('quick_order.request_quote_cta', 'Request a Quote')
                                        : (submitLabel || t('quick_order.continue_default', 'Continue with this package'))}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            );
        }

        // Default (authenticated wizard) layout — unchanged
        return (
            <Card data-testid="qoflow-step-review">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <Badge
                                className="mb-2 text-white border-0 shadow-sm"
                                style={{ backgroundImage: GOLD }}
                            >
                                Quick Order
                            </Badge>
                            <CardTitle className="flex items-center gap-2">
                                <Sparkles className="h-5 w-5 text-amber-500" />
                                {t('quick_order.review_title', 'Review Your Package')}
                            </CardTitle>
                            <CardDescription>{t('quick_order.review_desc', 'Confirm your selection before continuing')}</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="max-w-xl mx-auto py-4 space-y-4">
                        <div className="bg-white rounded-2xl p-6 border border-border">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <Badge className={`mb-2 ${tier.className}`}>{tier.label}</Badge>
                                    <h3 className="text-xl font-bold text-slate-900">{selectedBundle.title}</h3>
                                    <p className="text-sm text-slate-500 mt-1">
                                        {quantity} copies · {selectedBundle.format_label}
                                    </p>
                                    {requireTitle && title && (
                                        <p className="text-xs text-slate-500 mt-2">{t('quick_order.release_prefix', 'Release:')} <span className="font-medium text-slate-700">{title}</span></p>
                                    )}
                                    {selectedBundle.description && (
                                        <p className="text-sm text-slate-600 mt-2 leading-relaxed whitespace-pre-line">
                                            {selectedBundle.description}
                                        </p>
                                    )}
                                </div>
                                {showPrices && (price?.requires_quote ? (
                                    <div className="text-right max-w-[200px]">
                                        <Badge className="bg-amber-200 text-amber-900 border-amber-300">
                                            Quote Required
                                        </Badge>
                                        <p className="text-[11px] text-amber-800 mt-1 leading-snug">
                                            {t('quick_order.quote_required_msg_short', 'This configuration needs a custom quote.')}
                                        </p>
                                    </div>
                                ) : price ? (
                                    <div className="text-right">
                                        <p className="text-3xl font-bold text-slate-900 font-display" data-testid="quick-review-unit-price">
                                            {formatKr(price.unit, 2, price.unitRaw)} <span className="text-base font-medium text-slate-500">/ unit</span>
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1" data-testid="quick-review-total">{formatKr(price.total, 0, price.totalRaw)} total</p>
                                    </div>
                                ) : null)}
                            </div>

                            {showPrices && price?.requires_quote && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4" data-testid="qoflow-review-quote-required">
                                    <p className="text-sm text-amber-900 mb-2">
                                        {t('quick_order.quote_required_msg', 'Your configuration includes items that require a custom quote. Submit a request and our team will send you a priced quote.')}
                                    </p>
                                    {(price.quote_gated_skus || []).slice(0, 3).map((g, i) => (
                                        <p key={i} className="text-[11px] text-amber-800">
                                            • {g.procurement_item_name || g.display_name || g.sku}
                                            {g.trigger === 'procurement_bom' ? ' (supplier quote needed)' : ''}
                                        </p>
                                    ))}
                                    <label className="block mt-3 text-xs font-medium text-amber-900">
                                        {t('quick_order.quote_comment', 'Add a note for our team (optional)')}
                                    </label>
                                    <textarea
                                        value={quoteComment}
                                        onChange={(e) => setQuoteComment(e.target.value)}
                                        rows={3}
                                        placeholder={t('quick_order.quote_comment_ph', 'Deadline, special requests, etc.')}
                                        className="mt-1 w-full text-sm border border-amber-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                                        data-testid="qoflow-quote-comment"
                                        maxLength={2000}
                                    />
                                </div>
                            )}

                            <div className="bg-slate-50 rounded-lg px-4 py-3 mb-4 border border-slate-100">
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1.5">
                                    Estimated timeline
                                </p>
                                {isVinyl && (
                                    <div className="flex justify-between text-xs">
                                        <span className="text-slate-500">Test press</span>
                                        <span className="text-slate-700 font-medium">~4 weeks</span>
                                    </div>
                                )}
                                <div className={`flex justify-between text-xs ${isVinyl ? 'mt-1' : ''}`}>
                                    <span className="text-slate-500">Production</span>
                                    <span className="text-slate-700 font-medium">{isVinyl ? '~8 weeks' : '~2 weeks'}</span>
                                </div>
                            </div>

                            <button
                                onClick={() => setShowDetails(!showDetails)}
                                className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-2"
                            >
                                {showDetails ? 'Hide details' : "View what's included"}
                                <ChevronRight className={`h-4 w-4 transition-transform ${showDetails ? 'rotate-90' : ''}`} />
                            </button>
                            {showDetails && (
                                <ul className="text-sm text-slate-600 space-y-1 mt-2">
                                    {selectedBundle.bundled_options &&
                                        Object.entries(selectedBundle.bundled_options).map(([key, value]) => (
                                            <li key={key} className="flex items-center gap-2">
                                                <Check className="h-3 w-3 text-emerald-500" />
                                                <span className="capitalize">{key.replace(/_/g, ' ')}: </span>
                                                <span className="font-medium text-slate-700">{String(value)}</span>
                                            </li>
                                        ))}
                                </ul>
                            )}
                        </div>

                        <div className="flex items-center justify-between pt-2">
                            <Button variant="ghost" onClick={() => setStep(1)} data-testid="qoflow-review-back">
                                <ChevronLeft className="h-4 w-4 mr-1" /> Change quantity
                            </Button>
                            <Button
                                onClick={handleFinalContinue}
                                disabled={!price}
                                className="text-white border-0 shadow-md hover:shadow-lg hover:brightness-105 transition-all"
                                style={{ backgroundImage: GOLD }}
                                data-testid="qoflow-confirm"
                            >
                                <Check className="h-4 w-4 mr-2" />
                                {price?.requires_quote
                                    ? t('quick_order.request_quote_cta', 'Request a Quote')
                                    : (submitLabel || t('quick_order.continue_default', 'Continue with this package'))}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return null;
}

// Re-export the Zap icon so this module's intent is clear elsewhere
export { Zap };
